import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { renderPartnerLogo, partnersContent } from './utils';

const EventPartnersSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Technical Partner
  const technicalPartners = [
    {
      id: 1,
      name: "فريق وجد الفني",
      partnerType: "technical",
      logo: "/assets/logos/Logo-png-Black.png",
      logoAlt: "فريق وجد الفني",
      website: "https://www.instagram.com/wajd.team/",
      description: "فريق فني ثقافي تطوّعي، يعمل على جمع الفنانين السوريين وتنمية مواهبهم وتمثيلهم في الفعاليات والمعارض داخل سوريا وفي بلدان اللجوء. يستمدّ الفريق هويته من رسالته الفنية والتطوعية، ويقوم على إدارة منتخبة تُعنى بتنظيم العمل ورعاية المبادرات الإبداعية.\n\nيسعى فريق وَجد إلى أن يكون منصة حقيقية لدعم الفنّانين، والدفاع عن مساحة الفنّ السوري الحر، وتمثيل قضاياه أمام المؤسسات الثقافية والجمهور في مختلف أنحاء العالم، عبر تنظيم المعارض، إطلاق المبادرات، وتمكين المواهب الشابة"
    }
  ];

  // Cultural Partner
  const culturalPartners = [
    {
      id: 1,
      name: "جمعية مشكاة الشبابية",
      partnerType: "cultural",
      logo: "/assets/logos/mishka.jpg",
      logoAlt: "جمعية مشكاة الشبابية",
      website: "https://www.instagram.com/mishkahyouth/"
    }
  ];

  const content = partnersContent?.[currentLanguage];

  return (
    <section id="event-partners" className="relative py-16 lg:py-24 px-4 bg-gradient-to-b from-[#114845]/5 via-[#114845]/3 to-background overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-24 lg:mb-32"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-accent/10 rounded-full blur-lg"></div>
                <div className="relative w-12 h-12 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full flex items-center justify-center border border-accent/30 shadow-memorial">
                  <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <h3 className="font-memorial text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-accent via-primary to-success bg-clip-text text-transparent drop-shadow-sm">
                {content?.eventPartnersTitle}
              </span>
            </h3>
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center justify-center gap-2"
            >
              <div className="h-1 w-12 bg-gradient-to-r from-transparent to-accent rounded-full"></div>
              <div className="h-1 w-2 bg-accent rounded-full"></div>
              <div className="h-1 w-12 bg-gradient-to-l from-transparent to-accent rounded-full"></div>
            </motion.div>
          </motion.div>

          {/* Partners Grid - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Technical Partner Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h4 className="font-body text-xl md:text-2xl font-semibold text-primary mb-6 text-center">
                {currentLanguage === 'ar' ? 'الشريك الفني' : currentLanguage === 'en' ? 'Technical Partner' : 'Teknik Ortak'}
              </h4>
              {technicalPartners.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  className="bg-background border-2 border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 w-full text-center"
                >
                  {item.logo && item.logo !== "" ? (
                    <>
                      <div className={`flex items-center justify-center ${item.isLarge ? 'h-48' : 'h-40'}`}>
                        {renderPartnerLogo(item)}
                      </div>
                      <p className="text-base md:text-2xl font-medium text-gray-800 mt-3">
                        {item.name}
                      </p>
                      {item.description && (
                        <p className="text-sm md:text-base text-gray-600 mt-4 text-center leading-relaxed whitespace-pre-line">
                          {item.description}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-lg md:text-xl">{item.name}</div>
                      {item.description && (
                        <p className="text-sm md:text-base text-gray-600 mt-4 text-center leading-relaxed whitespace-pre-line">
                          {item.description}
                        </p>
                      )}
                    </>
                  )}
                </motion.div>
              ))}
            </motion.div>

            {/* Cultural Partner Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h4 className="font-body text-xl md:text-2xl font-semibold text-primary mb-6 text-center">
                {currentLanguage === 'ar' ? 'الشريك الثقافي' : currentLanguage === 'en' ? 'Cultural Partner' : 'Kültürel Ortak'}
              </h4>
              {culturalPartners.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  className="bg-background border-2 border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 w-full text-center"
                >
                  {item.logo && item.logo !== "" ? (
                    <>
                      <div className={`flex items-center justify-center ${item.isLarge ? 'h-48' : 'h-40'}`}>
                        {renderPartnerLogo(item)}
                      </div>
                      <p className="text-base md:text-2xl font-medium text-gray-800 mt-3">
                        {item.name}
                      </p>
                      {item.description && (
                        <p className="text-sm md:text-base text-gray-600 mt-4 text-center leading-relaxed whitespace-pre-line">
                          {item.description}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-lg md:text-xl">{item.name}</div>
                      {item.description && (
                        <p className="text-sm md:text-base text-gray-600 mt-4 text-center leading-relaxed whitespace-pre-line">
                          {item.description}
                        </p>
                      )}
                    </>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EventPartnersSection;

